import {ClipboardEvent} from 'react';
import {priority} from '@priority-software/priority-proto-api/lib';
import {isValid, isValidTime} from '@priority-software/priority-utils/lib/date';
import {Column, Form, formsStore} from '../../../stores/Form';
import {isValid as isValidNumber} from '@priority-software/priority-utils/lib/number';
import {RecordEditState, UpdateActions} from '../../../stores/Form/enums';
import {getStringFormatByColumnType, isDate, localizedWeekDays} from './date';
import {isTime, isValidTimeSum} from './time';
import {getLang} from '../../../Localization';
import {getThousandSign} from './number';

export const checkTabsOnly = (inputString: string) => {
    return /^\t*$/.test(inputString);
};

export const spreadsheetToArray = (spreadsheetData: string) => {
    const tabRegex = /\t/g;
    const newlineRegex = /\r\n|\n\r|\n|\r/g;
    const dupedArray = spreadsheetData
        .split(newlineRegex)
        .filter(item => !checkTabsOnly(item))
        .map(item => item.split(tabRegex)); //creating arrays of rows
    // handle pastes of multiline cells
    return dupedArray;
};

export const isExcelClipboardData = e => {
    const text: string = getClipboardData(e);
    return ['\n', '\t', '\r'].some(element => text.includes(element));
};

export const extractValuesFromSpreadsheet = e => {
    const pastedText = getClipboardData(e);

    return spreadsheetToArray(pastedText || '');
};

export const getClipboardData = (e: ClipboardEvent<Element>) => {
    const clipboardData =
        e.clipboardData || (e as ClipboardEvent<HTMLElement>).nativeEvent?.clipboardData;

    return clipboardData.getData('Text') || clipboardData.getData('text/plain');
};

const reg = /[?*]/;
export const isValidDateInput = (value: string, format: string): boolean => {
    return isValid(value, format) && !reg.test(value);
};

type IsValidateDateTime = {
    value: string;
    format: string;
    columnType: priority.netitems.form.ColumnType;
};

export const isValidDateTime = ({value, format, columnType}: IsValidateDateTime) => {
    if (!value.includes('?') && !value.includes('*')) {
        if (isDate(columnType)) {
            return isValidDateInput(value, format);
        } else {
            return isValidTime(value, format);
        }
    }
    return false;
};

export const checkValueOnColumnType = (column: Column, cellValue: string, lang: string) => {
    if (isDate(column.Type) || isTime(column.Type)) {
        const format = getStringFormatByColumnType(column.Type, lang);
        if (!isValidDateTime({value: cellValue, format, columnType: column.Type})) {
            return false;
        }
    }
    if (column.Type === priority.netitems.form.ColumnType.Time6) {
        if (!isValidTimeSum(cellValue)) return false;
    }

    if (column.Type === priority.netitems.form.ColumnType.Day) {
        if (!localizedWeekDays.find(day => day.startsWith(cellValue))) {
            return false;
        }
    }
    if (
        column.Type === priority.netitems.form.ColumnType.Real ||
        column.Type === priority.netitems.form.ColumnType.Int ||
        column.Type === priority.netitems.form.ColumnType.Unsigned
    ) {
        const isPositive = column.Type === priority.netitems.form.ColumnType.Unsigned;
        if (
            !isValidNumber(
                cellValue,
                column.decimal,
                column.maxLength,
                isPositive,
                getThousandSign()
            )
        ) {
            return false;
        }
    }
    if (column.Type === priority.netitems.form.ColumnType.Boolean) {
        return false;
    }
    return true;
};

const pasteToCell = async (form: Form, rowData: string[], currentCol: number) => {
    const {currentScreen} = formsStore;
    const lang = getLang();

    for (let i = 0; i < rowData.length; i++) {
        form.setCurrentColumnIdByIndex(i + currentCol);

        if (rowData[i] && !form.currentColumn.isReadonly) {
            let cellValue = rowData[i];
            const column = form.currentColumn;
            if (column.maxLength < cellValue.length) {
                cellValue = cellValue.slice(0, column.maxLength);
            }

            const isValidValue = checkValueOnColumnType(column, cellValue, lang);

            if (!isValidValue) {
                return false;
            }

            form.activeField().setCurrentValue(cellValue);
            const res = await currentScreen.checkAndSave(UpdateActions.update);

            if (!res) return false;
        }
    }
    return true;
};

export const pasteToColumn = async e => {
    const {currentScreen} = formsStore;
    const form = currentScreen.getActiveForm();
    const {currentRow, currentColumn} = form;
    const currentCol = currentColumn.index;

    let i = 0;

    const dataExcel = extractValuesFromSpreadsheet(e);
    for await (const rowData of dataExcel) {
        if (typeof rowData !== 'object') break;

        const res = await pasteToCell(form, rowData, currentCol);
        if (!res) break;

        i++;
        const rowIdx = i + currentRow;
        if (currentScreen.editMode === RecordEditState.update && dataExcel.length !== 1) {
            const saveRow = await currentScreen.checkAndSave(UpdateActions.save);
            if (!saveRow) break;
        }
        if (dataExcel.length !== i) {
            form.insertEmptyRecord(rowIdx);
            form.setCurrentRow(rowIdx);
        }
    }
};

export const pasteToCellF10 = async (form: Form) => {
    if (form.currentColumn.isReadonly) {
        return;
    }

    const field = form.records[form.currentRow - 1]?.getField(form?.currentColumnId);

    if (field.Value) {
        form.activeField().setCurrentValue(field.currentValue);
        form.updateRecordValues(form.currentRow, form.currentRecord.fields);
    }
};
